import { ref, computed, type Ref, type ComputedRef } from "vue";
import { defineStore } from "pinia";
import type { User, LoginResponse } from "@/types/models";
import {
  startSession,
  clearSession,
  getUserFromSession,
} from "@/utils/session";
// Removed unused import: TIME
import { logger } from "@/utils/logger";
import { setUser, clearUser, addBreadcrumb } from "@/modules/sentry";
import {
  initiateOAuthLogin,
  handleOAuthCallback,
  storeOAuthTokens,
  clearOAuthTokens,
  isOAuthEnabled,
  getUserInfo,
  getOAuthAccessToken,
  getOAuthRefreshToken,
  isOAuthTokenExpired,
  refreshAccessToken,
  type OAuthUserInfo, initiateOAuthLogout,
} from '@/service/http/oauthService';
interface AuthStore {
  // State
  isAuthenticated: Ref<boolean>;
  user: Ref<User | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;

  // Computed
  isLoggedIn: ComputedRef<boolean>;
  userRole: ComputedRef<string | null>;
  userName: ComputedRef<string | null>;
  isOAuthEnabled: ComputedRef<boolean>;

  // Actions
  loginWithOAuth: (returnUrl?: string) => Promise<void>;
  handleOAuthCallback: (urlParams: URLSearchParams) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  clearAuth: () => void;
  checkAuth: () => Promise<boolean>;
  initializeAuth: () => Promise<void>;
}

export const useAuth = defineStore("auth", (): AuthStore => {
  // Reactive state
  const isAuthenticated: Ref<boolean> = ref(false);
  const user: Ref<User | null> = ref(null);
  const loading: Ref<boolean> = ref(false);
  const error: Ref<string | null> = ref(null);

  // Computed properties
  const isLoggedIn = computed(() => isAuthenticated.value);
  const userRole = computed(() => user.value?.role || null);
  const userName = computed(() => user.value?.username || null);
  const oauthEnabled = computed(() => isOAuthEnabled());

  // --- Cross-tab Synchronization ---
  // Storage listener for cross-tab synchronization
  const storageListener: Ref<((event: StorageEvent) => void) | null> =
    ref(null);

  /**
   * Handle storage events from other tabs
   * Syncs authentication state when user logs in/out in another tab
   */
  function handleStorageChange(event: StorageEvent): void {
    // Only handle relevant keys
    if (!event.key || !["user", "oauth_access_token"].includes(event.key)) {
      return;
    }

    logger.debug("Storage change detected:", event.key);

    // If user or token was removed (logout in another tab)
    if (
      (event.key === "user" || event.key === "oauth_access_token") &&
      !event.newValue
    ) {
      // Clear auth state without triggering another storage event
      isAuthenticated.value = false;
      user.value = null;
      error.value = null;
      clearUser();

      // Redirect to login
      window.location.href = "/auth/login";
      return;
    }

    // If user was added (login in another tab)
    if (event.key === "user" && event.newValue) {
      // Re-check authentication to sync state
      checkAuth();
    }
  }

  /**
   * Attach storage change listener for cross-tab sync
   */
  function attachStorageListener(): void {
    if (storageListener.value) return;

    storageListener.value = handleStorageChange;
    window.addEventListener("storage", storageListener.value);
  }

  /**
   * Detach storage change listener
   */
  function detachStorageListener(): void {
    if (!storageListener.value) return;

    window.removeEventListener("storage", storageListener.value);
    storageListener.value = null;
  }

  // --- End Cross-tab Synchronization ---

  // Authentication methods
  const clearAuth = (): void => {
    // Add breadcrumb before clearing
    addBreadcrumb({
      category: "auth",
      message: "User logged out or session cleared",
      level: "info",
    });

    isAuthenticated.value = false;
    user.value = null;
    error.value = null;
    clearSession();
    clearOAuthTokens();
    detachStorageListener();

    // Clear user context in Sentry
    clearUser();
  };

  const logoutUser = async (): Promise<void> => {
    try {
      // Clear auth immediately - no artificial delay needed
      const returnUrl = new URL(window.location.href);

      returnUrl.pathname = "/auth/login";

      await initiateOAuthLogout(returnUrl.toString())
      clearAuth();
    } catch (err) {
      logger.error("Logout error:", err);
      // Ensure auth is cleared even if error occurs
      clearAuth();
    }
  };

  const checkAuth = async (): Promise<boolean> => {
    try {
      // First check traditional session (no idle timeout check)
      const userData = getUserFromSession();
      if (userData) {
        user.value = userData;
        isAuthenticated.value = true;
        attachStorageListener();

        // Restore user context in Sentry from session
        setUser({
          id: userData.id?.toString(),
          username: userData.username,
          extra: {
            tenantId: userData.officeId?.toString(),
            roles: [userData.role],
            officeId: userData.officeId,
            officeName: userData.officeName,
            role: userData.role,
          },
        });

        return true;
      }

      // Check OAuth token if traditional session is not valid
      let oauthToken = getOAuthAccessToken();
      const refreshToken = getOAuthRefreshToken();

      // If token is expired or missing but we have refresh token, try to refresh it
      if (refreshToken && (!oauthToken || isOAuthTokenExpired())) {
        try {
          logger.debug(
            "OAuth token expired or missing, attempting to refresh...",
          );
          const newTokens = await refreshAccessToken(refreshToken);
          storeOAuthTokens(newTokens);
          oauthToken = newTokens.access_token;
          logger.debug("OAuth token refreshed successfully");
        } catch (err) {
          logger.error("Failed to refresh OAuth token:", err);
          clearAuth();
          return false;
        }
      }

      if (oauthToken && !isOAuthTokenExpired()) {
        // Try to get user info from session or reconstruct from OAuth
        const existingUserData = getUserFromSession();
        if (existingUserData) {
          user.value = existingUserData;
          isAuthenticated.value = true;
          attachStorageListener();

          setUser({
            id: existingUserData.id?.toString(),
            username: existingUserData.username,
            extra: {
              tenantId: existingUserData.officeId?.toString(),
              roles: [existingUserData.role],
            },
          });

          return true;
        }

        // If we have OAuth token but no user data in session, fetch user info
        try {
          const tokens = {
            access_token: oauthToken,
            token_type: "Bearer",
          };
          const userInfo = await getUserInfo(tokens);

          const reconstructedUser: User = {
            id: 0,
            username:
              userInfo.preferred_username || userInfo.email || userInfo.sub,
            role: "user",
            officeId: 0,
            officeName: userInfo.name || "OAuth User",
            permissions: [],
            base64EncodedAuthenticationKey: oauthToken,
            authenticated: true,
            shouldRenewPassword: false,
            isTwoFactorAuthenticationRequired: false,
          };

          user.value = reconstructedUser;
          isAuthenticated.value = true;
          startSession(reconstructedUser as unknown as Record<string, unknown>);
          attachStorageListener();

          setUser({
            id: userInfo.sub,
            username: reconstructedUser.username,
            ...(userInfo.email && { email: userInfo.email }),
            extra: {
              tenantId: reconstructedUser.officeId?.toString(),
              roles: [reconstructedUser.role],
            },
          });

          return true;
        } catch (err) {
          logger.error("Failed to fetch user info from OAuth token:", err);
          clearAuth();
          return false;
        }
      }

      // No valid session or OAuth token
      clearAuth();
      return false;
    } catch (err) {
      clearAuth();
      return false;
    }
  };

  const initializeAuth = async (): Promise<void> => {
    await checkAuth();
  };

  const loginWithOAuth = async (returnUrl?: string): Promise<void> => {
    try {
      loading.value = true;
      error.value = null;

      // Add breadcrumb for OAuth login attempt
      addBreadcrumb({
        category: "auth",
        message: "Initiating OAuth login",
        level: "info",
      });

      await initiateOAuthLogin(returnUrl);
    } catch (err) {
      error.value = (err as Error)?.message || "OAuth login failed";
      loading.value = false;
      throw err;
    }
  };

  const handleOAuthCallbackFn = async (
    urlParams: URLSearchParams,
  ): Promise<LoginResponse> => {
    try {
      loading.value = true;
      error.value = null;

      // Handle OAuth callback and get tokens
      const tokens = await handleOAuthCallback(urlParams);

      // Store tokens
      storeOAuthTokens(tokens);

      // Fetch user profile from OAuth provider
      let userInfo: OAuthUserInfo;
      try {
        userInfo = await getUserInfo(tokens);
        logger.debug("OAuth user info:", userInfo);
      } catch (userInfoError) {
        logger.error("Failed to fetch user info:", userInfoError);
        // If user info fetch fails, try to extract from ID token
        throw new Error(
          "Failed to retrieve user information from OAuth provider",
        );
      }

      // Map OAuth user info to application User model
      const userData: User = {
        id: 0, // Will be set by backend or parsed from sub
        username: userInfo.preferred_username || userInfo.email || userInfo.sub,
        role: "user", // Default role, should be fetched from backend based on OAuth user
        officeId: 0,
        officeName: userInfo.name || "OAuth User",
        permissions: [], // Should be fetched from backend
        base64EncodedAuthenticationKey: tokens.access_token,
        authenticated: true,
        shouldRenewPassword: false,
        isTwoFactorAuthenticationRequired: false,
      };

      user.value = userData;
      isAuthenticated.value = true;
      startSession(userData as unknown as Record<string, unknown>);
      attachStorageListener();

      // Set user context in Sentry
      setUser({
        id: userInfo.sub,
        username: userData.username,
        ...(userInfo.email && { email: userInfo.email }),
        extra: {
          tenantId: userData.officeId?.toString(),
          roles: [userData.role],
        },
      });

      // Add breadcrumb for successful OAuth login
      addBreadcrumb({
        category: "auth",
        message: "OAuth login successful",
        level: "info",
        data: {
          username: userData.username,
          email: userInfo.email,
        },
      });

      return { success: true, user: userData };
    } catch (err) {
      error.value = (err as Error)?.message || "OAuth authentication failed";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    isAuthenticated,
    user,
    loading,
    error,
    isLoggedIn,
    userRole,
    userName,
    isOAuthEnabled: oauthEnabled,
    loginWithOAuth,
    handleOAuthCallback: handleOAuthCallbackFn,
    logout: logoutUser,
    clearAuth,
    checkAuth,
    initializeAuth,
  };
});
